import { useId, useLayoutEffect, useRef, useState } from 'react';

import { useStrings } from '../i18n/index.ts';
import type { CardInput } from '../state/payment.ts';
import { BankBadge } from './BankBadge.tsx';
import { Button, cx, FieldBox } from './kit.tsx';
import { PayMarks } from './PayMarks.tsx';

/**
 * Форма новой карты: номер, срок, CVV, имя на карте; под формой знаки платёжных систем и банков,
 * как требует банк на странице ввода карты. Номер уходит из браузера прямо эквайеру
 * (`payWithNewCard`), через наш сервер не проходит. Проверки до отправки: Luhn, срок в будущем,
 * CVV из 3–4 цифр, имя не пустое.
 */

function luhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return digits.length >= 13 && sum % 10 === 0;
}

/** Платёжная система по первым цифрам: для бейджа у поля номера. */
export function brandOf(digits: string): string | null {
  if (/^4/.test(digits)) return 'VISA';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'MASTERCARD';
  if (/^(50|56|57|58|6)/.test(digits)) return 'MAESTRO';
  if (/^3[47]/.test(digits)) return 'AMEX';
  if (/^9891/.test(digits)) return 'DINACARD';
  return null;
}

/** Цифры срока из поля: автозаполнение браузера подставляет «MM/YYYY», берутся месяц и две последние цифры года. */
function expiryDigits(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length === 6) return d.slice(0, 2) + d.slice(4);
  return d.slice(0, 4);
}

/** «∙∙/∙∙» с введёнными цифрами на месте точек: видно, сколько осталось ввести. */
function maskedExpiry(digits: string): string {
  const at = (i: number) => digits[i] ?? '∙';
  return `${at(0)}${at(1)}/${at(2)}${at(3)}`;
}

/** Курсор сразу за последней введённой цифрой, разделитель перепрыгивается. */
function placeExpiryCaret(input: HTMLInputElement | null, digits: string): void {
  if (!input) return;
  const pos = digits.length + (digits.length >= 2 ? 1 : 0);
  requestAnimationFrame(() => input.setSelectionRange(pos, pos));
}

function groupDigits(digits: string): string {
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

export function CardForm(props: { amountText: string; busy: boolean; owner: string; onSubmit: (card: CardInput) => void; submitTitle?: string; note?: string }) {
  const strings = useStrings();
  const id = useId();
  const [pan, setPan] = useState('');
  /** Цифры срока без разделителя; в поле они показываются по маске «∙∙/∙∙». */
  const [expiry, setExpiry] = useState('');
  const expiryInput = useRef<HTMLInputElement | null>(null);
  const [cvv, setCvv] = useState('');
  const [owner, setOwner] = useState(props.owner);
  const [touched, setTouched] = useState(false);

  const digits = pan.replace(/\D/g, '');
  const brand = brandOf(digits);
  const month = expiry.length === 4 ? Number(expiry.slice(0, 2)) : 0;
  const year = expiry.length === 4 ? 2000 + Number(expiry.slice(2)) : 0;
  const now = new Date();
  const expiryValid = month >= 1 && month <= 12 && (year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1));
  const panValid = luhn(digits);
  const cvvValid = /^\d{3,4}$/.test(cvv);
  const ownerValid = owner.trim().length >= 2;
  const valid = panValid && expiryValid && cvvValid && ownerValid;

  useLayoutEffect(() => {
    if (document.activeElement === expiryInput.current) placeExpiryCaret(expiryInput.current, expiry);
  }, [expiry]);

  const submit = () => {
    setTouched(true);
    if (!valid || props.busy) return;
    props.onSubmit({ pan: digits, expMonth: month, expYear: year, cvv, owner: owner.trim() });
  };

  return (
    <form
      className="stack card-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <FieldBox label={strings.cardNumber} invalid={touched && !panValid}>
        <input
          id={`${id}-pan`}
          className="t-numeric"
          inputMode="numeric"
          autoComplete="cc-number"
          placeholder="0000 0000 0000 0000"
          maxLength={23}
          value={pan}
          autoFocus
          onChange={(event) => setPan(groupDigits(event.target.value.replace(/\D/g, '').slice(0, 19)))}
        />
        {brand && <BankBadge issuer={null} brand={brand} size={20} />}
      </FieldBox>
      <div className="card-form-row">
        <FieldBox label={strings.cardExpiry} invalid={touched && !expiryValid}>
          <input
            ref={expiryInput}
            className={cx('t-numeric', expiry.length === 0 && 'card-expiry-empty')}
            inputMode="numeric"
            autoComplete="cc-exp"
            value={maskedExpiry(expiry)}
            onKeyDown={(event) => {
              if (event.key === 'Backspace') {
                event.preventDefault();
                setExpiry((d) => d.slice(0, -1));
              }
            }}
            onChange={(event) => setExpiry(expiryDigits(event.target.value))}
            onFocus={() => placeExpiryCaret(expiryInput.current, expiry)}
            onClick={() => placeExpiryCaret(expiryInput.current, expiry)}
          />
        </FieldBox>
        <FieldBox label="CVV" invalid={touched && !cvvValid}>
          <input className="t-numeric" type="password" inputMode="numeric" autoComplete="cc-csc" placeholder="•••" maxLength={4} value={cvv} onChange={(event) => setCvv(event.target.value.replace(/\D/g, '').slice(0, 4))} />
        </FieldBox>
      </div>
      <FieldBox label={strings.cardOwner} invalid={touched && !ownerValid}>
        <input type="text" autoComplete="cc-name" autoCapitalize="characters" placeholder="PETAR PETROVIĆ" value={owner} onChange={(event) => setOwner(event.target.value)} />
      </FieldBox>
      <Button busy={props.busy} disabled={touched && !valid} onClick={submit}>
        {props.submitTitle ?? strings.payAmount(props.amountText)}
      </Button>
      {props.note && <p className="t-footnote t-muted">{props.note}</p>}
      <PayMarks />
    </form>
  );
}
